import { useQuery } from '@tanstack/react-query';
import type { AxiosInstance } from 'axios';
import { SilentError } from 'init/silent-error';
import {
  type ApiError,
  addResourceNameToQueryResult,
  handleZodError,
  type QueryOptions,
  useAxiosInstance,
} from 'shared/hooks/utils';
import { PaginationSchemaV2 } from 'types/pagination';
import { z } from 'zod/v4';

const URL = '/task_types';

export const TaskTypeSchema = z.object({
  id: z.number(),
  uid: z.string(),
  name: z.string(),
  isDeletable: z.boolean(),
  defaultTitle: z.string().nullable(),
  defaultDescription: z.string().nullable(),
  defaultDueOffsetDays: z.number().nullable(),
});

export type TaskType = z.infer<typeof TaskTypeSchema>;

const LoadTaskTypesResponseSchema = z.object({
  data: z.array(TaskTypeSchema),
  pagination: PaginationSchemaV2,
});

export type LoadTaskTypesResponse = z.infer<typeof LoadTaskTypesResponseSchema>;

export async function loadTaskTypesQueryFn(axiosInstance: Promise<AxiosInstance>) {
  const instance = await axiosInstance;
  const { data } = await instance.get(URL);

  const parseResult = LoadTaskTypesResponseSchema.safeParse(data);
  if (!parseResult.success) {
    handleZodError(parseResult, 'taskTypes');
    throw new SilentError();
  }

  return parseResult.data;
}

const emptyTaskTypes: LoadTaskTypesResponse = { data: [], pagination: { totalItems: 0 } };

export function useLoadTaskTypes(options: QueryOptions<LoadTaskTypesResponse> = {}) {
  const axiosInstance = useAxiosInstance();

  const queryResult = useQuery<LoadTaskTypesResponse, ApiError>({
    queryKey: ['taskTypes'],
    queryFn: () => loadTaskTypesQueryFn(axiosInstance),
    placeholderData: previousData => previousData ?? emptyTaskTypes,
    ...options,
  });

  return {
    ...addResourceNameToQueryResult<
      LoadTaskTypesResponse,
      ApiError,
      'taskTypes',
      'PreventUndefined'
    >('taskTypes', queryResult, emptyTaskTypes),
    taskTypes: queryResult.data ?? emptyTaskTypes,
  };
}
